import React, { useState, useEffect } from 'react';
import './App.css';
import Main from './pages/Main';
import Transactions from './pages/Transactions';
import Settings from './pages/Settings';
import { api } from './utils/api';

function App() {
  const [activeTab, setActiveTab] = useState('main');
  const [authStatus, setAuthStatus] = useState(null);
  const [appName, setAppName] = useState('Budget Tracker');
  const [currentMonth, setCurrentMonth] = useState(() => {
    const now = new Date();
    return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
  });

  useEffect(() => {
    checkAuthStatus();
    loadSettings();
  }, []);

  async function checkAuthStatus() {
    try {
      const status = await api.getAuthStatus();
      setAuthStatus(status);
    } catch (error) {
      console.error('Auth check failed:', error);
    }
  }

  async function loadSettings() {
    try {
      const settings = await api.getSettings();
      
      // Update app name
      if (settings.app_name) {
        setAppName(settings.app_name);
        document.title = settings.app_name;
      }
      
      // Update favicon
      if (settings.favicon) {
        const link = document.getElementById('favicon');
        link.href = settings.favicon;
      }
    } catch (error) {
      console.error('Failed to load settings:', error);
    }
  }

  async function handleLogin() {
    try {
      const { url } = await api.loginWithGoogle();
      window.location.href = url;
    } catch (error) {
      alert('Login failed: ' + error.message);
    }
  }

  async function handleLogout() {
    try {
      await api.logout();
      setAuthStatus({ configured: true, authenticated: false });
    } catch (error) {
      alert('Logout failed: ' + error.message);
    }
  }

  // Handle OAuth callback
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const code = params.get('code');
    
    if (code) {
      api.handleGoogleCallback(code)
        .then(() => {
          window.history.replaceState({}, '', '/');
          checkAuthStatus();
        })
        .catch(error => {
          console.error('OAuth callback failed:', error);
          window.history.replaceState({}, '', '/');
        });
    }
  }, []);

  if (authStatus === null) {
    return (
      <div className="app loading">
        <div className="spinner"></div>
      </div>
    );
  }

  if (authStatus.configured && !authStatus.authenticated) {
    return (
      <div className="app">
        <div className="login-container">
          <h1>{appName}</h1>
          <p>Please sign in to continue</p>
          <button onClick={handleLogin} className="btn btn-primary">
            Sign in with Google
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="app">
      <header className="app-header">
        <div className="header-content">
          <h1>{appName}</h1>
          <div className="header-controls">
            <input
              type="month"
              value={currentMonth}
              onChange={(e) => setCurrentMonth(e.target.value)}
              className="month-picker"
            />
            {authStatus.authenticated && (
              <div className="user-menu">
                <img src={authStatus.user.picture} alt="User" className="user-avatar" />
                <button onClick={handleLogout} className="btn-logout">Logout</button>
              </div>
            )}
          </div>
        </div>
        <nav className="tabs">
          <button
            className={`tab ${activeTab === 'main' ? 'active' : ''}`}
            onClick={() => setActiveTab('main')}
          >
            Main
          </button>
          <button
            className={`tab ${activeTab === 'transactions' ? 'active' : ''}`}
            onClick={() => setActiveTab('transactions')}
          >
            Transactions
          </button>
          <button
            className={`tab ${activeTab === 'settings' ? 'active' : ''}`}
            onClick={() => setActiveTab('settings')}
          >
            Settings
          </button>
        </nav>
      </header>

      <main className="app-main">
        {activeTab === 'main' && <Main currentMonth={currentMonth} />}
        {activeTab === 'transactions' && <Transactions currentMonth={currentMonth} />}
        {activeTab === 'settings' && <Settings onUpdate={loadSettings} />}
      </main>
    </div>
  );
}

export default App;
